'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, FormGroup, FormInputErrors, Input } from '~/components/primitives';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useI18n } from '~/features/i18n';
import { Icon } from '~/components/primitives';
import { cx } from 'class-variance-authority';
import type { I18n } from '@funkey/locales';
import { I18N_CONF } from '@funkey/locales/config';
import { z } from 'zod';
import { useFormState } from '~/modules/form';
import { useSignInMutation } from '~/features/auth/login';
import { Auth } from '~/modules/auth/client';
import type { Auth_SignInRoute } from '~/modules/auth/staff';
import { AUTH_REDIRECT_PARAM, Auth_parseRedirectUrl } from '~/modules/auth/redirect';
import { action_getSignInRoute } from './actions';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
export const LoginForm: React.FC = _props => {
  const $ = _c(72);
  const i18n = useI18n();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("error") as any || null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const authErrorCode = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      email: "",
      password: ""
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== i18n) {
    t2 = i18n._("The email is not valid.");
    $[3] = i18n;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      validate: _temp,
      message: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== i18n) {
    t4 = i18n._("Password is required.");
    $[7] = i18n;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t4) {
    t5 = {
      validate: _temp2,
      message: t4
    };
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t5) {
    t6 = {
      email: t3,
      password: t5
    };
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const formState = useFormState(t1, t6);
  const router = useRouter();
  const [signInRoute, setSignInRoute] = useState(null);
  const [isRouting, setIsRouting] = useState(false);
  let t7;
  if ($[14] !== i18n.locale || $[15] !== searchParams) {
    t7 = Auth_parseRedirectUrl(searchParams.get(AUTH_REDIRECT_PARAM) ?? searchParams.get("callbackUrl")) ?? {
      locale: i18n.locale
    };
    $[14] = i18n.locale;
    $[15] = searchParams;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t7) {
    t8 = {
      callbackURL: t7
    };
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const signIn = useSignInMutation(t8);
  let t9;
  if ($[19] !== authErrorCode || $[20] !== i18n) {
    t9 = authErrorCode ? <LoginPageError i18n={i18n} title={i18n._("Something went wrong")} errorCode={authErrorCode} className="mb-4" /> : null;
    $[19] = authErrorCode;
    $[20] = i18n;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== formState || $[23] !== i18n.locale || $[24] !== router || $[25] !== signIn || $[26] !== signInRoute) {
    t10 = async e => {
      e.preventDefault();
      if (!signInRoute) {
        if (!z.string().email().safeParse(formState.values.email.trim()).success) {
          return;
        }
        setIsRouting(true);
        const route = await action_getSignInRoute(formState.values.email).finally(() => setIsRouting(false));
        if (route.kind === "staff") {
          router.push(`/${i18n.locale}/admin/login`);
          return;
        }
        if (route.kind === "bizz") {
          router.push(`/${i18n.locale}/bizz/login`);
          return;
        }
        setSignInRoute(route);
        return;
      }
      if (!formState.validate()) {
        return;
      }
      await signIn.mutateAsync(formState.values);
    };
    $[22] = formState;
    $[23] = i18n.locale;
    $[24] = router;
    $[25] = signIn;
    $[26] = signInRoute;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== i18n) {
    t11 = i18n._("Email");
    $[28] = i18n;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== formState) {
    t12 = formState.getNativeInputProps("email");
    $[30] = formState;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const inputProps = t12;
  const originalOnChange = inputProps.onChange;
  let t13;
  if ($[32] !== originalOnChange) {
    t13 = e_0 => {
      originalOnChange(e_0);
      setSignInRoute(null);
    };
    $[32] = originalOnChange;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== inputProps || $[35] !== t13) {
    t14 = {
      ...inputProps,
      onChange: t13
    };
    $[34] = inputProps;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = t14;
  let t16;
  if ($[37] !== t11 || $[38] !== t15) {
    t16 = <Input placeholder={t11} {...t15} />;
    $[37] = t11;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== formState) {
    t17 = formState.getErrorMessages("email");
    $[40] = formState;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t17) {
    t18 = <FormInputErrors errors={t17} />;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t16 || $[45] !== t18) {
    t19 = <FormGroup>{t16}{t18}</FormGroup>;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== formState || $[48] !== i18n || $[49] !== signInRoute) {
    t20 = !!signInRoute && <FormGroup><Input type="password" autoFocus={true} placeholder={i18n._("Password")} {...formState.getNativeInputProps("password")} /><FormInputErrors errors={formState.getErrorMessages("password")} /></FormGroup>;
    $[47] = formState;
    $[48] = i18n;
    $[49] = signInRoute;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = signIn.isPending || isRouting;
  const t22 = signIn.isPending || isRouting;
  let t23;
  if ($[51] !== i18n || $[52] !== signInRoute) {
    t23 = signInRoute ? i18n._("Sign in") : i18n._("Next step");
    $[51] = i18n;
    $[52] = signInRoute;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t22 || $[56] !== t23) {
    t24 = <Button type="submit" variant="tertiary" size="lg" isLoading={t21} disabled={t22} className="w-full sm:w-auto">{t23}</Button>;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== i18n || $[59] !== signInRoute) {
    t25 = !!signInRoute && <Link href={I18N_CONF.getTranslatedUrl("forgot-password", {
      locale: i18n.locale
    })} className="block text-sm font-medium text-turqoise-500 hover:underline">{i18n._("Forgot password?")}</Link>;
    $[58] = i18n;
    $[59] = signInRoute;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t24 || $[62] !== t25) {
    t26 = <div className="flex flex-col items-center justify-between gap-6 sm:mb-6 sm:flex-row">{t24}{t25}</div>;
    $[61] = t24;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t10 || $[65] !== t19 || $[66] !== t20 || $[67] !== t26) {
    t27 = <form data-testid="login-form" onSubmit={t10}>{t19}{t20}{t26}</form>;
    $[64] = t10;
    $[65] = t19;
    $[66] = t20;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t27 || $[70] !== t9) {
    t28 = <>{t9}{t27}</>;
    $[69] = t27;
    $[70] = t9;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  return t28;
};

// TODO: clean up
type SignInErrorTypes = 'Signin' | 'OAuthSignin' | 'OAuthCallback' | 'OAuthCreateAccount' | 'EmailCreateAccount' | 'Callback' | 'OAuthAccountNotLinked' | 'NotAuthorized' | 'EmailSignin' | 'CredentialsSignin' | 'SessionRequired';
const LoginPageError: React.FC<{
  title: string | React.ReactNode;
  errorCode: SignInErrorTypes | null;
  className?: string;
  i18n: I18n;
}> = props => {
  const $ = _c(25);
  let t0;
  if ($[0] !== props.className) {
    t0 = cx("rounded-3xl bg-red-50 p-4", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="shrink-0"><Icon size="sm" name="exclamation-circle" className="text-red-400" aria-hidden="true" /></div>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.title) {
    t2 = <h3 className="text-sm font-medium text-salmon-800">{props.title}</h3>;
    $[3] = props.title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  bb0: switch (props.errorCode) {
    case "Signin":
    case "OAuthSignin":
    case "OAuthCallback":
    case "OAuthCreateAccount":
    case "EmailCreateAccount":
    case "Callback":
      {
        let t4;
        if ($[5] !== props.i18n) {
          t4 = props.i18n._("Try signing in with a different account.");
          $[5] = props.i18n;
          $[6] = t4;
        } else {
          t4 = $[6];
        }
        t3 = t4;
        break bb0;
      }
    case "OAuthAccountNotLinked":
      {
        let t4;
        if ($[7] !== props.i18n) {
          t4 = props.i18n._("To confirm your identity, sign in with the same account you used originally.");
          $[7] = props.i18n;
          $[8] = t4;
        } else {
          t4 = $[8];
        }
        t3 = t4;
        break bb0;
      }
    case "NotAuthorized":
      {
        let t4;
        if ($[9] !== props.i18n) {
          t4 = props.i18n._("You are not authorized to access this page.");
          $[9] = props.i18n;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        t3 = t4;
        break bb0;
      }
    case "EmailSignin":
      {
        let t4;
        if ($[11] !== props.i18n) {
          t4 = props.i18n._("The e-mail could not be sent.");
          $[11] = props.i18n;
          $[12] = t4;
        } else {
          t4 = $[12];
        }
        t3 = t4;
        break bb0;
      }
    case "CredentialsSignin":
      {
        let t4;
        if ($[13] !== props.i18n) {
          t4 = props.i18n._("Sign in failed. Check the details you provided are correct.");
          $[13] = props.i18n;
          $[14] = t4;
        } else {
          t4 = $[14];
        }
        t3 = t4;
        break bb0;
      }
    case "SessionRequired":
      {
        let t4;
        if ($[15] !== props.i18n) {
          t4 = props.i18n._("Please sign in to access this page.");
          $[15] = props.i18n;
          $[16] = t4;
        } else {
          t4 = $[16];
        }
        t3 = t4;
        break bb0;
      }
    case null:
      {
        t3 = null;
        break bb0;
      }
    default:
      {
        t3 = null;
      }
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <div className="mt-2 text-sm text-salmon-700"><p>{t3}</p></div>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t2 || $[20] !== t4) {
    t5 = <div className="flex">{t1}<div className="ml-3">{t2}{t4}</div></div>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== t0 || $[23] !== t5) {
    t6 = <div className={t0}>{t5}</div>;
    $[22] = t0;
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
};
export const LoggedIn: React.FC<{
  locale: I18N_CONF.Locale;
}> = props => {
  const $ = _c(22);
  const i18n = useI18n();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get("error") as any || null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const authErrorCode = t0;
  let t1;
  if ($[2] !== props.locale) {
    t1 = async () => {
      await Auth.signOut({
        redirectTo: I18N_CONF.getTranslatedUrl("login", {
          locale: props.locale
        })
      });
    };
    $[2] = props.locale;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleLogout = t1;
  let t2;
  if ($[4] !== authErrorCode || $[5] !== i18n) {
    t2 = authErrorCode ? <LoginPageError i18n={i18n} title={i18n._("Something went wrong")} errorCode={authErrorCode} className="mb-4" /> : null;
    $[4] = authErrorCode;
    $[5] = i18n;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== i18n) {
    t3 = i18n._("You are already logged in. Please log out to sign in with a different account.");
    $[7] = i18n;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <p className="text-gray-600">{t3}</p>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== i18n) {
    t5 = i18n._("Log out");
    $[11] = i18n;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== handleLogout || $[14] !== t5) {
    t6 = <Button onClick={handleLogout} variant="tertiary" size="lg" iconBefore="logout" className="w-full sm:w-auto">{t5}</Button>;
    $[13] = handleLogout;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t4 || $[17] !== t6) {
    t7 = <div className="space-y-4">{t4}{t6}</div>;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t2 || $[20] !== t7) {
    t8 = <>{t2}{t7}</>;
    $[19] = t2;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
function _temp(value) {
  return z.string().min(1).email().trim().safeParse(value).success;
}
function _temp2(value_0) {
  return z.string().min(1).trim().safeParse(value_0).success;
}